import { Link } from "react-router-dom";
import { Mail, Phone, ArrowLeft } from "lucide-react";
import { BRAND } from "../lib/gomill-data";
import { Overline, Section } from "../components/Bits";

const SECTIONS = [
  {
    title: "1. Information We Collect",
    intro: "When you interact with our website, we may collect:",
    groups: [
      { title: "Personal Information", items: ["Name", "Phone Number", "Email Address", "City or Location", "Business Information (for franchise inquiries)"] },
      { title: "Technical Information", items: ["IP Address", "Browser Type", "Device Information", "Website Usage Data", "Cookies and Analytics Information"] },
    ],
  },
  {
    title: "2. How We Use Your Information",
    intro: "We use the information collected to:",
    items: [
      "Respond to your inquiries and requests",
      "Process franchise and business partnership inquiries",
      "Provide customer support",
      "Improve our website and services",
      "Share important updates about GoMill",
      "Maintain website security and prevent misuse",
    ],
  },
  {
    title: "3. Cookies & Analytics",
    body: "Our website may use cookies and analytics tools to understand visitor behavior and improve user experience. You can disable cookies through your browser settings; however, some website features may not function properly.",
  },
  {
    title: "4. Information Sharing",
    intro: "GoMill does not sell, rent, or trade your personal information to third parties. We may share information only:",
    items: [
      "With authorized service providers supporting our operations",
      "When required by applicable law or government authorities",
      "To protect our legal rights and website security",
    ],
  },
  {
    title: "5. Data Security",
    body: "We implement reasonable technical and organizational measures to protect your information from unauthorized access, disclosure, alteration, or destruction. While we strive to protect your data, no internet transmission or storage system can be guaranteed to be 100% secure.",
  },
  {
    title: "6. Third-Party Links",
    body: "Our website may contain links to third-party websites or social media platforms. GoMill is not responsible for the privacy practices or content of those external websites.",
  },
  {
    title: "7. Children's Privacy",
    body: "Our website is not intended for children under the age of 13. We do not knowingly collect personal information from children.",
  },
  {
    title: "8. Your Rights",
    intro: "You may request to:",
    items: [
      "Access your personal information",
      "Correct inaccurate information",
      "Delete your information where legally permissible",
      "Withdraw consent for communications",
    ],
    footer: "To make such requests, please contact us using the details below.",
  },
  {
    title: "9. Changes to This Policy",
    body: "GoMill may update this Privacy Policy from time to time. Any updates will be posted on this page along with the revised effective date.",
  },
];

export default function PrivacyPolicy() {
  return (
    <>
      <Section>
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gomill-muted hover:text-gomill-green mb-6" data-testid="privacy-back">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
        <Overline className="mb-4">Legal</Overline>
        <h1 className="font-serif text-5xl md:text-6xl text-gomill-green leading-[1.05] tracking-tight">Privacy Policy</h1>
        <p className="mt-3 text-sm text-gomill-muted uppercase tracking-widest">Last Updated: June 2026</p>
        <p className="mt-6 text-lg text-gomill-muted max-w-3xl">
          Welcome to GoMill. We value your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, and safeguard information shared through our website.
        </p>
      </Section>

      <Section className="!pt-0">
        <div className="max-w-3xl space-y-10">
          {SECTIONS.map((s) => (
            <div key={s.title} className="bg-white border border-gomill-border rounded-3xl p-6 md:p-8">
              <h2 className="font-serif text-2xl md:text-3xl text-gomill-green leading-tight">{s.title}</h2>
              {s.intro && <p className="text-gomill-muted mt-3">{s.intro}</p>}
              {s.body && <p className="text-gomill-text leading-relaxed mt-3">{s.body}</p>}
              {s.items && (
                <ul className="mt-4 space-y-2 list-disc list-inside text-gomill-text">
                  {s.items.map((item) => <li key={item}>{item}</li>)}
                </ul>
              )}
              {s.groups && s.groups.map((g) => (
                <div key={g.title} className="mt-5">
                  <div className="font-serif text-lg text-gomill-green mb-2">{g.title}</div>
                  <ul className="space-y-1.5 list-disc list-inside text-gomill-text">
                    {g.items.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </div>
              ))}
              {s.footer && <p className="text-gomill-muted mt-4 text-sm">{s.footer}</p>}
            </div>
          ))}

          {/* Contact card */}
          <div className="bg-gomill-green text-white rounded-3xl p-6 md:p-8" data-testid="privacy-contact">
            <h2 className="font-serif text-2xl md:text-3xl text-gomill-mustard leading-tight">10. Contact Us</h2>
            <p className="mt-3 text-white/80">For questions regarding this Privacy Policy or your personal information, please contact:</p>
            <div className="mt-5 space-y-2 text-white/90">
              <div className="font-serif text-xl">GoMill</div>
              <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 hover:text-gomill-mustard">
                <Phone className="w-4 h-4 text-gomill-mustard" /> {BRAND.phone}
              </a>
              <a href={`mailto:${BRAND.email}`} className="flex items-center gap-2 hover:text-gomill-mustard">
                <Mail className="w-4 h-4 text-gomill-mustard" /> {BRAND.email}
              </a>
            </div>
          </div>

          <p className="text-gomill-muted text-sm pt-4">By using this website, you agree to the terms outlined in this Privacy Policy.</p>
        </div>
      </Section>
    </>
  );
}
